import type { MouseEvent } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import Bulletin from '../bulletin/Bulletin';
import XoopsPathRedirect from '../common/XoopsPathRedirect';
import Config, { type MultiLang } from '../config';
import Credits from '../credits/Credits';
import Functions from '../functions';
import Pico from '../pico/Pico';
import XooNIpsIndexTreeBlock from '../xoonips/blocks/IndexTree';
import XooNIpsSearchBlock from '../xoonips/blocks/Search';
import Xoonips from '../xoonips/Xoonips';
import RssBlock from './blocks/RssBlock';
import ContactUs from './ContactUs';
import Footer from './Footer';
import Header from './Header';
import RssFeedSites from './RssFeedSites';
import Sitemap from './Sitemap';
import TopPage from './TopPage';
import Xsearch from './Xsearch';

interface Props {
    lang: MultiLang;
}

const hackathonPathes = ['hackathon2018', 'brainhack-global-2017', 'hackathon2016', 'bah2015'];
const ainiPathes = ['2014', '2015', '2016', '2017', '2018'];
const nbniPathes = ['2014', '2017'];

// `/modules/<dirname>/...` addresses of the pico and bulletin modules are served without the prefix.
const ModulePathRedirect = () => {
    const { pathname, search, hash } = useLocation();
    return <Navigate to={pathname.replace(/^\/modules/, '') + search + hash} replace />;
};

const XoonipsPage = (props: Props) => {
    const { lang } = props;
    return (
        <div className="col2">
            <div className="leftcolumn">
                <div className="block">
                    <div className="blockTitle">Item Search</div>
                    <div className="blockContent">
                        <XooNIpsSearchBlock lang={lang} />
                    </div>
                </div>
                <div className="block">
                    <div className="blockTitle">Index Tree</div>
                    <div className="blockContent">
                        <XooNIpsIndexTreeBlock lang={lang} />
                    </div>
                </div>
            </div>
            <div className="centercolumn">
                <Xoonips lang={lang} />{' '}
            </div>
        </div>
    );
};

const PlatformUpdates = (props: Props) => {
    const { lang } = props;
    return (
        <>
            <h2 id="link-pf-update">
                {Functions.mlang('[en]Platform Updates[/en][ja]プラットフォーム更新情報[/ja]', lang)}
            </h2>
            <div className="centercolumn">
                <div className="centerLcolumn">
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Neuro-Imaging Platform', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.nimg]} num={2} />
                        </div>
                    </div>
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Dynamic Brain Platform', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.dynamicbrain]} num={2} />
                        </div>
                    </div>
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Brain Transcriptome Database', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.cdtdb]} num={2} />
                        </div>
                    </div>
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Invertebrate Brain Platform', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.invbrain]} num={2} />
                        </div>
                    </div>
                </div>
                <div className="centerRcolumn">
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Visiome Platform', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.visiome]} num={2} />
                        </div>
                    </div>
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Cerebellar Platform', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.cerebellum]} num={2} />
                        </div>
                    </div>

                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Mouse Phenotype Database', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.mpdb]} num={2} />
                        </div>
                    </div>
                    <div className="block">
                        <div className="blockTitle">{Functions.mlang('Brain Machine Interface Platform', lang)}</div>
                        <div className="blockContent">
                            <RssBlock lang={lang} sites={[RssFeedSites.bmi]} num={2} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const Page = (props: Props) => {
    const { lang } = props;
    const { pathname } = useLocation();
    const goPageTop = Functions.mlang('[en]Go Page Top[/en][ja]ページの先頭へ[/ja]', lang);
    return (
        <div id="page">
            <Header lang={lang} />
            <div id="main">
                <Routes>
                    <Route path="/" element={<TopPage lang={lang} />} />
                    <Route path="/contactus" element={<ContactUs lang={lang} />} />
                    <Route path="/sitemap" element={<Sitemap lang={lang} />} />
                    <Route path="/modules/xsearch" element={<Xsearch lang={lang} />} />
                    <Route path="/modules/credits/*" element={<Credits lang={lang} />} />
                    {ainiPathes.map((name) => (
                        <Route
                            key={`aini${name}`}
                            path={`/aini${name}/*`}
                            element={<Pico lang={lang} dirname="aini" virtualpath="/aini" showTitle={false} />}
                        />
                    ))}
                    {nbniPathes.map((name) => (
                        <Route
                            key={`nbni${name}`}
                            path={`/nbni${name}/*`}
                            element={<Pico lang={lang} dirname="nbni" virtualpath="/nbni" showTitle={false} />}
                        />
                    ))}
                    {hackathonPathes.map((name) => (
                        <Route
                            key={name}
                            path={`/${name}/*`}
                            element={<Pico lang={lang} dirname="hackathon" virtualpath="" showTitle={false} />}
                        />
                    ))}
                    {Config.PICO_MODULES.map((name) => (
                        <Route
                            key={name}
                            path={`/${name}/*`}
                            element={<Pico lang={lang} dirname={name} virtualpath={`/${name}/`} showTitle={false} />}
                        />
                    ))}
                    {Config.PICO_MODULES.map((name) => (
                        <Route key={`modules-${name}`} path={`/modules/${name}/*`} element={<ModulePathRedirect />} />
                    ))}
                    {Config.BULLETIN_MODULES.map((name) => (
                        <Route key={name} path={`/${name}/*`} element={<Bulletin lang={lang} mydirname={name} />} />
                    ))}
                    {Config.BULLETIN_MODULES.map((name) => (
                        <Route key={`modules-${name}`} path={`/modules/${name}/*`} element={<ModulePathRedirect />} />
                    ))}
                    <Route path="/modules/xoonips/*" element={<XoonipsPage lang={lang} />} />
                    <Route path="*" element={<XoopsPathRedirect lang={lang} />} />
                </Routes>
                {pathname === '/resource/platform.html' && <PlatformUpdates lang={lang} />}
                <div className="goPageTop">
                    <a
                        href="/"
                        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            window.scrollTo(0, 0);
                        }}
                    >
                        {goPageTop}
                    </a>
                </div>
            </div>
            <Footer lang={lang} />
        </div>
    );
};

export default Page;
